import type { InventoryFormValues, InventoryLookups } from '../../types/inventoryForm.types';

interface BasicInfoSectionProps {
    values: InventoryFormValues;
    lookups?: InventoryLookups;
    onChange: <K extends keyof InventoryFormValues>(key: K, value: InventoryFormValues[K]) => void;
    isEdit?: boolean;
}

function FieldLabel({ text, required = false }: { text: string; required?: boolean }) {
    return (
        <label className="mb-2 block sm:text-sm text-xs text-slate-700">
            {text}
            {required ? <span className="text-red-500"> *</span> : null}
        </label>
    );
}

export function BasicInfoSection({ values, lookups, onChange, isEdit }: BasicInfoSectionProps) {
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3 space-y-4">
            <h3 className="sm:text-sm text-xs text-slate-900 border-b border-slate-100 pb-2">Thông tin cơ bản</h3>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="space-y-1">
                    <FieldLabel text="Mã vật tư" required={!isEdit} />
                    <input
                        value={values.code}
                        onChange={(event) => onChange('code', event.target.value)}
                        disabled={isEdit}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
                        placeholder="Nhập mã vật tư"
                    />
                    {!isEdit && <p className="text-xs text-slate-500 italic">Tự động tạo nếu để trống</p>}
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Tên vật tư" required />
                    <input
                        value={values.name}
                        onChange={(event) => onChange('name', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="Nhập tên vật tư"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Danh mục vật tư" required />
                    <select
                        value={values.category_name}
                        onChange={(event) => onChange('category_name', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                    >
                        <option value="">Chọn danh mục</option>
                        {(lookups?.categoryOptions ?? []).map((option, index) => (
                            <option key={`${option.value}-${index}`} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>

                <div className="space-y-1">
                    <FieldLabel text="Đơn vị tính" required />
                    <select
                        value={values.unit}
                        onChange={(event) => onChange('unit', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                    >
                        <option value="">Chọn đơn vị tính</option>
                        {(lookups?.unitOptions ?? []).map((option, index) => (
                            <option key={`${option.value}-${index}`} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Quy cách / Mô tả" />
                    <input
                        value={values.specification}
                        onChange={(event) => onChange('specification', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="Nhập quy cách, kích thước..."
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Loại vật tư" />
                    <div className="flex items-center gap-6 h-10">
                        <label className="flex items-center gap-2 sm:text-sm text-xs text-slate-700 cursor-pointer">
                            <input
                                type="radio"
                                checked={values.item_type === 'main'}
                                onChange={() => onChange('item_type', 'main')}
                                className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                            />
                            Vật tư chính
                        </label>
                        <label className="flex items-center gap-2 sm:text-sm text-xs text-slate-700 cursor-pointer">
                            <input
                                type="radio"
                                checked={values.item_type === 'sub'}
                                onChange={() => onChange('item_type', 'sub')}
                                className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                            />
                            Vật tư phụ
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
}
